import { type Ref, ref } from "vue";

import { type Nullable } from "shared/types/utility";

import useServerSentEvents, { type ErrorResponse } from "./useServerSentEvents";

export interface AIAssistantStream {
  booleanQuery: string;
  data: AIAssistantStreamData;
  error: boolean;
  input: string;
  json: string;
  loading: boolean;
  message: string;
  rateLimitReached: boolean;
}

export interface AIAssistantOptions {
  beta?: boolean;
  json?: boolean;
  source?: string;
  url?: string;
}

export interface AIAssistantStreamData {
  ai_feature_configuration_id: Nullable<number>;
  group_id: Nullable<number>;
  model_chat_history_id?: number;
  source: string;
  text?: string;
}

interface AIAssistantStreamJSON {
  text: string;
  booleanQuery: string;
}

export function useAIAssistantStream(
  data: Ref<AIAssistantStreamData>,
  prompt: string,
  options: AIAssistantOptions = {}
) {
  const booleanQuery = ref<string>("");
  const error = ref<boolean>(false);
  const json = ref<string>("");
  const loading = ref<boolean>(true);
  const message = ref<string>("");
  const rateLimitReached = ref<boolean>(false);
  const input = ref<string>(prompt);

  function parseText(jsonString: string): AIAssistantStreamJSON {
    try {
      return JSON.parse(jsonString || "");
    } catch {
      error.value = true;

      return { text: "", booleanQuery: "" };
    }
  }

  function handleStreamStart() {
    message.value = "";
    loading.value = true;
  }

  function handleStreamSuccess(responseData: AIAssistantStreamData) {
    Object.assign(data.value, {
      ...responseData,
    });

    if (options.json) {
      json.value += responseData.text || "";
    } else {
      message.value += responseData.text || "";
    }
  }

  function handleError(errorResponse: ErrorResponse) {
    if (errorResponse.code === 429) {
      rateLimitReached.value = true;
    }

    error.value = true;
    loading.value = false;
  }

  function handleStreamEnd() {
    loading.value = false;

    if (options.json) {
      const parsedText = parseText(json.value);

      message.value = parsedText.text;
      booleanQuery.value = cleanBooleanQuery(parsedText.booleanQuery || "");
    }
  }

  function cleanBooleanQuery(query: string) {
    return query
      .replace(/[“”‟❝❞]/g, '"') // replace fancy quote with standard "
      .replace(/\\+"/g, '"');
  }

  useServerSentEvents<AIAssistantStreamData>({
    url: options.url || "ai/services/general_assistant",
    payload: JSON.stringify({
      beta: options.beta,
      group_id: data.value?.group_id,
      include_rag: false,
      prompt,
    }),
    onSuccess: handleStreamSuccess,
    onError: handleError,
    onStreamStart: handleStreamStart,
    onStreamEnd: handleStreamEnd,
  });

  return {
    booleanQuery,
    data,
    error,
    input,
    json,
    loading,
    message,
    rateLimitReached,
  };
}
